import { defineMessages } from 'react-intl';
import _isEmpty from 'lodash/isEmpty';

import { NAV_ICONS } from 'constants/icons';
import LINKS from 'constants/links';
import { MANAGE_SERVICES_TAB } from 'constants/logic';
import { experienceAllows, NAV_KEY } from 'constants/venue-experience';
import { isSmartRateFeatureEnabled } from 'features/SmartRate/SmartRate.helpers';
import { getActiveVenue } from 'helpers/active-venue';
import { IA_KEY, iaLogic, shouldShowMenu, shouldShowMenuEditor } from 'helpers/ia-logic';
import { makePath, testPath } from 'helpers/link-generation';

const navMessages = defineMessages({
  account: {
    id: 'navigation-labels--account',
    defaultMessage: 'Account',
  },
  appointments: {
    id: 'navigation-labels--appointments',
    defaultMessage: 'Appointments',
  },
  calendarSync: {
    id: 'navigation-labels--calsync',
    defaultMessage: 'Calendar Sync',
  },
  campaigns: {
    id: 'navigation-labels--campaigns',
    defaultMessage: 'Campaigns',
  },
  classes: {
    id: 'navigation-labels--classes',
    defaultMessage: 'Schedule',
  },
  contactUs: {
    id: 'navigation-labels--contact-us',
    defaultMessage: 'Contact us',
  },
  // We need to use a separate message because the navigation is rendered by message ID,
  // and we want to use different access rules for Japanese venues
  contactUsJapan: {
    id: 'navigation-labels--contact-us-japan',
    defaultMessage: 'Contact us',
  },
  createSchedules: {
    id: 'navigation-labels--create-schedules',
    defaultMessage: 'Schedule settings',
  },
  dynamicPricing: {
    id: 'navigation-labels--dynamic-pricing',
    defaultMessage: 'Dynamic pricing',
  },
  editClassPage: {
    id: 'navigation-labels--edit-class-page',
    defaultMessage: 'Classes',
  },
  editBusinessProfile: {
    id: 'navigation-labels--business-profile',
    defaultMessage: 'Business profile',
  },
  feedback: {
    id: 'navigation-labels--feedback',
    defaultMessage: 'Feedback',
  },
  home: {
    id: 'navigation-labels--home',
    defaultMessage: 'Home',
  },
  instructors: {
    id: 'navigation-labels--instructors',
    defaultMessage: 'Instructors',
  },
  integrations: {
    id: 'navigation-labels--integrations',
    defaultMessage: 'Integrations',
  },
  items: {
    id: 'navigation-labels--items',
    defaultMessage: 'Items',
  },
  menu: {
    id: 'navigation-labels--menu',
    defaultMessage: 'Menu',
  },
  menuEditor: {
    id: 'navigation-labels--menu-editor',
    defaultMessage: 'Menu editor',
  },
  logOut: {
    id: 'navigation-labels--log-out',
    defaultMessage: 'Log out',
  },
  manage: {
    id: 'navigation-labels--manage',
    defaultMessage: 'Manage',
  },
  opportunities: {
    id: 'navigation-labels--opportunities',
    defaultMessage: 'Opportunities',
  },
  orders: {
    id: 'navigation-labels--orders',
    defaultMessage: 'Orders',
  },
  skuOrders: {
    id: 'navigation-labels--sku-orders',
    defaultMessage: 'Orders',
  },
  performance: {
    id: 'navigation-labels--performance',
    defaultMessage: 'Performance',
  },
  skuPerformance: {
    id: 'navigation-labels--sku-performance',
    defaultMessage: 'Performance',
  },
  payoutReports: {
    id: 'navigation-labels--payout-reports',
    defaultMessage: 'Payout reports',
  },
  premiumCaps: {
    id: 'navigation-labels--premium-caps',
    defaultMessage: 'Premium caps',
  },
  ratings: {
    id: 'navigation-labels--ratings-reviews',
    defaultMessage: 'Ratings & reviews',
  },
  stats: {
    id: 'navigation-labels--stats',
    defaultMessage: 'Stats',
  },
  resources: {
    id: 'navigation-labels--resources',
    defaultMessage: 'Resources',
  },
  settings: {
    id: 'navigation-labels--settings',
    defaultMessage: 'Settings',
  },
  services: {
    id: 'navigation-labels--services',
    defaultMessage: 'Services',
  },
  practitioners: {
    id: 'navigation-labels--practitioners',
    defaultMessage: 'Practitioners',
  },
  equipment: {
    id: 'navigation-labels--equipment',
    defaultMessage: 'Equipment',
  },
  smartRate: {
    id: 'navigation-labels--smart-rate',
    defaultMessage: 'SmartRate',
  },
  smartSpot: {
    id: 'navigation-labels--smart-spot',
    defaultMessage: 'SmartSpot',
  },
  smartTools: {
    id: 'navigation-labels--smart-tools',
    defaultMessage: 'SmartTools',
  },
  staff: {
    id: 'navigation-labels--staff',
    defaultMessage: 'Staff',
  },
  support: {
    id: 'navigation-labels--support',
    defaultMessage: 'Support',
  },
  notifications: {
    id: 'navigation-labels--notifications',
    defaultMessage: 'Notifications',
  },
});

const userShouldSeeManageServices = (userInfo, activeVenueId) => {
  return (
    iaLogic.userHasAccess(IA_KEY.MANAGE_SERVICES)(userInfo, activeVenueId) &&
    !iaLogic.featureIsSupported(IA_KEY.ITEM_MANAGEMENT)(userInfo, activeVenueId)
  );
};

const hasNestedSmartToolsNavAccess = (userInfo, activeVenueId) =>
  iaLogic.featureIsSupported(IA_KEY.SMART_SPOT)(userInfo, activeVenueId) &&
  iaLogic.featureIsSupported(IA_KEY.SMART_RATE)(userInfo, activeVenueId);

const smartRateNavUserHasFeature = (userInfo, _, activeVenueId) => {
  const userId = userInfo?.userId;
  const { countryCodeShort } = getActiveVenue(userInfo, activeVenueId);

  return userId ? isSmartRateFeatureEnabled(userId, activeVenueId, countryCodeShort) : false;
};

const smartSpotStandaloneNavUserHasFeature = async (userInfo, _, activeVenueId) => {
  if (!hasNestedSmartToolsNavAccess(userInfo, activeVenueId)) {
    return true;
  }

  const userId = userInfo?.userId;
  const { countryCodeShort } = getActiveVenue(userInfo, activeVenueId);
  const optimizelyEnabled = userId ? await isSmartRateFeatureEnabled(userId, activeVenueId, countryCodeShort) : false;

  return !optimizelyEnabled;
};

/**
 * When path is a function, Sidenav invokes it with this object (see Sidenav.js `navPathObject`).
 * @typedef {{ venueId: number, classId?: number, locale: string, userId: number }} NavPathObject
 */
export const NAV_CONFIG = [
  {
    message: navMessages.home,
    navKey: NAV_KEY.HOME,
    path: ({ venueId }) => makePath.home(venueId),
    icon: NAV_ICONS.home,
  },
  {
    message: navMessages.classes,
    navKey: NAV_KEY.CLASSES,
    path: ({ venueId }) => makePath.classes({ venueId }),
    icon: NAV_ICONS.calendar,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.YIELD_MANAGEMENT),
    testPath: testPath.classes,
  },
  {
    message: navMessages.appointments,
    navKey: NAV_KEY.APPOINTMENTS,
    path: ({ venueId }) => makePath.appointmentsCalendar({ venueId }),
    icon: NAV_ICONS.appointments,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.APPOINTMENTS),
    testPath: testPath.appointments,
  },
  {
    message: navMessages.orders,
    navKey: NAV_KEY.ORDERS,
    path: ({ venueId }) => makePath.appointments(venueId),
    icon: NAV_ICONS.appointments,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.ORDERS),
    testPath: testPath.appointments,
  },
  {
    message: navMessages.skuOrders,
    navKey: NAV_KEY.SKU_ORDERS,
    path: ({ venueId }) => makePath.orders(venueId),
    icon: NAV_ICONS.appointments,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.SKU_ORDERS),
    testPath: testPath.orders,
  },
  {
    message: navMessages.items,
    navKey: NAV_KEY.ITEMS,
    path: ({ venueId }) => makePath.items(venueId),
    icon: NAV_ICONS.tag,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.ITEM_MANAGEMENT),
  },
  {
    message: navMessages.menuEditor,
    navKey: NAV_KEY.MENU_EDITOR,
    path: ({ venueId }) => makePath.items(venueId),
    icon: NAV_ICONS.notepad,
    userHasAccess: shouldShowMenuEditor,
  },
  {
    message: navMessages.menu,
    navKey: NAV_KEY.MENU,
    path: ({ venueId }) => makePath.items(venueId),
    icon: NAV_ICONS.notepad,
    userHasAccess: shouldShowMenu,
  },
  {
    message: navMessages.calendarSync,
    navKey: NAV_KEY.CALENDAR_SYNC,
    path: ({ venueId }) => makePath.appointmentsCalSync(venueId),
    icon: NAV_ICONS.calsync,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.APPOINTMENTS_CALENDAR_LINK),
    testPath: testPath.appointmentsCalSync,
  },
  {
    message: navMessages.performance,
    navKey: NAV_KEY.PERFORMANCE,
    path: makePath.performance,
    icon: NAV_ICONS.chart,
    children: [
      {
        message: navMessages.opportunities,
        navKey: NAV_KEY.OPPORTUNITIES,
        path: ({ venueId }) => makePath.opportunities(venueId),
        userHasAccess: iaLogic.featureIsSupported(IA_KEY.OPPORTUNITIES),
        testPath: testPath.opportunities,
      },
      {
        message: navMessages.stats,
        navKey: NAV_KEY.STATS,
        path: ({ venueId }) => makePath.stats('your-insights', { venueId }),
        userHasAccess: (userInfo, activeVenueId) =>
          iaLogic.featureIsSupported(IA_KEY.NESTED_STATS)(userInfo, activeVenueId) &&
          iaLogic.featureIsSupported(IA_KEY.OPPORTUNITIES)(userInfo, activeVenueId),
        testPath: testPath.stats,
      },
    ],
  },
  {
    message: navMessages.skuPerformance,
    navKey: NAV_KEY.SKU_PERFORMANCE,
    path: () => makePath.skuPayoutReports(),
    icon: NAV_ICONS.chart,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.SKU_PAYOUT_REPORTS),
  },
  {
    message: navMessages.editBusinessProfile,
    navKey: NAV_KEY.SKU_BUSINESS_PROFILE,
    path: ({ venueId }) => makePath.editVenue(venueId),
    testPath: testPath.editBusinessProfile,
    icon: NAV_ICONS.businessProfile,
  },
  {
    message: navMessages.stats,
    navKey: NAV_KEY.STANDALONE_STATS,
    path: ({ venueId }) => makePath.statsDynamic(venueId),
    icon: NAV_ICONS.chart,
    userHasAccess: (userInfo, activeVenueId) =>
      !iaLogic.featureIsSupported(IA_KEY.SKU_VENUE)(userInfo, activeVenueId) &&
      (iaLogic.featureIsSupported(IA_KEY.STANDALONE_STATS)(userInfo, activeVenueId) ||
        !iaLogic.featureIsSupported(IA_KEY.OPPORTUNITIES)(userInfo, activeVenueId)),
    testPath: testPath.stats,
  },
  {
    message: navMessages.ratings,
    navKey: NAV_KEY.RATINGS,
    path: ({ venueId }) => makePath.ratings({ venueId }),
    testPath: testPath.ratings,
    icon: NAV_ICONS.star,
    userHasAccess: iaLogic.userHasAccess(IA_KEY.RATINGS),
  },
  {
    message: navMessages.campaigns,
    navKey: NAV_KEY.CAMPAIGNS,
    path: ({ venueId }) => makePath.campaigns({ venueId }),
    testPath: testPath.campaigns,
    icon: NAV_ICONS.speaker,
    userHasAccess: iaLogic.userHasAccess(IA_KEY.CAMPAIGNS),
  },
  {
    message: navMessages.smartTools,
    path: ({ venueId }) => makePath.smartTools(venueId),
    icon: NAV_ICONS.smartSpot,
    children: [
      {
        message: navMessages.smartRate,
        navKey: NAV_KEY.SMART_SPOT,
        path: ({ venueId }) => makePath.smartRate(venueId),
        userHasAccess: hasNestedSmartToolsNavAccess,
        userHasFeature: smartRateNavUserHasFeature,
        testPath: testPath.smartRate,
      },
      {
        message: navMessages.smartSpot,
        navKey: NAV_KEY.SMART_SPOT,
        path: ({ venueId }) => makePath.smartTools(venueId),
        userHasAccess: hasNestedSmartToolsNavAccess,
        userHasFeature: smartRateNavUserHasFeature,
        testPath: testPath.smartTools,
      },
    ],
  },
  {
    message: navMessages.smartSpot,
    navKey: NAV_KEY.SMART_SPOT,
    path: ({ venueId }) => makePath.smartTools(venueId),
    testPath: testPath.smartTools,
    icon: NAV_ICONS.smartSpot,
    userHasAccess: iaLogic.featureIsSupported(IA_KEY.SMART_SPOT),
    userHasFeature: smartSpotStandaloneNavUserHasFeature,
  },
  {
    message: navMessages.manage,
    navKey: NAV_KEY.MANAGE,
    icon: NAV_ICONS.notepad,
    children: [
      {
        message: navMessages.editBusinessProfile,
        navKey: NAV_KEY.BUSINESS_PROFILE,
        path: ({ venueId }) => makePath.editVenue(venueId),
        testPath: testPath.editBusinessProfile,
      },
      {
        message: navMessages.createSchedules,
        navKey: NAV_KEY.SCHEDULE_SETTINGS,
        path: ({ venueId }) => makePath.schedules({ venueId }),
        userHasAccess: iaLogic.userHasAccess(IA_KEY.MANAGE_CLASS_SCHEDULE),
        testPath: testPath.schedules,
      },
      {
        message: navMessages.editClassPage,
        navKey: NAV_KEY.EDIT_CLASS_PAGE,
        path: ({ venueId }) => makePath.editClass(venueId),
        userHasAccess: iaLogic.userHasAccess(IA_KEY.MANAGE_CLASS_PAGES),
        testPath: testPath.editClassPage,
      },
      {
        message: navMessages.services,
        navKey: NAV_KEY.SERVICES,
        path: ({ venueId }) => makePath.manageServices({ venueId, tab: MANAGE_SERVICES_TAB.SERVICES }),
        userHasAccess: userShouldSeeManageServices,
        testPath: testPath.services,
      },
      {
        message: navMessages.instructors,
        navKey: NAV_KEY.INSTRUCTORS,
        path: ({ venueId }) => makePath.instructors({ venueId }),
        userHasAccess: iaLogic.userHasAccess(IA_KEY.MANAGE_INSTRUCTORS),
        testPath: testPath.instructors,
      },
      {
        message: navMessages.practitioners,
        navKey: NAV_KEY.PRACTITIONERS,
        path: ({ venueId }) => makePath.manageServices({ venueId, tab: MANAGE_SERVICES_TAB.PRACTITIONERS }),
        userHasAccess: userShouldSeeManageServices,
        testPath: testPath.practitioners,
      },
      {
        message: navMessages.equipment,
        navKey: NAV_KEY.EQUIPMENT,
        path: ({ venueId }) => makePath.manageServices({ venueId, tab: MANAGE_SERVICES_TAB.RESOURCES }),
        userHasAccess: iaLogic.featureIsSupported(IA_KEY.EQUIPMENT),
        testPath: testPath.equipment,
      },
      {
        message: navMessages.dynamicPricing,
        navKey: NAV_KEY.DYNAMIC_PRICING,
        path: ({ venueId }) => makePath.editVenueDynamicPricing(venueId),
        userHasAccess: iaLogic.userHasAccess(IA_KEY.MANAGE_DYNAMIC_PRICING),
      },
      {
        message: navMessages.premiumCaps,
        navKey: NAV_KEY.PREMIUM_CAPS,
        path: ({ venueId }) => makePath.premiumCaps(venueId),
        userHasAccess: iaLogic.userHasAccess(IA_KEY.MANAGE_PREMIUM_CAPS),
      },
    ],
  },
  {
    message: navMessages.integrations,
    navKey: NAV_KEY.INTEGRATIONS,
    path: ({ venueId }) => makePath.integrations(venueId),
    icon: NAV_ICONS.link,
    fillIcon: true,
  },
  {
    message: navMessages.support,
    navKey: NAV_KEY.SUPPORT,
    icon: NAV_ICONS.question,
    children: [
      {
        message: navMessages.resources,
        path: LINKS.resources.href,
        isExternal: true,
        userHasFeature: (_, locale) => {
          return locale !== 'ja';
        },
      },
      {
        message: LINKS.faq.message,
        path: LINKS.faq.href,
        isExternal: true,
      },
      {
        message: navMessages.contactUsJapan,
        isExternal: true,
        path: LINKS.contactJapan.href,
        userHasAccess: iaLogic.featureIsSupported(IA_KEY.JAPAN_FEATURES),
      },
      {
        message: navMessages.contactUs,
        isExternal: false,
        path: undefined,
        searchParams: { chat_active: true },
        userHasAccess: (userInfo, activeVenueId) => {
          return !iaLogic.featureIsSupported(IA_KEY.JAPAN_FEATURES)(userInfo, activeVenueId);
        },
      },
      {
        message: navMessages.feedback,
        path: ({ userId }) => makePath.feedbackSurvey(userId),
        isExternal: true,
      },
    ],
  },
  {
    message: navMessages.account,
    navKey: NAV_KEY.ACCOUNT,
    icon: NAV_ICONS.cog,
    children: [
      {
        message: navMessages.settings,
        path: '/account/profile',
      },
      {
        message: navMessages.notifications,
        path: '/account/notifications',
      },
      {
        message: navMessages.staff,
        path: '/account/staff',
        userHasAccess: iaLogic.userHasAccess(IA_KEY.ACCOUNT_STAFF),
      },
      {
        message: navMessages.logOut,
        path: '/logout',
        testPath: testPath.logout,
      },
    ],
  },
  {
    // these are internal-only messages and don't need to be translated
    message: { id: '...', defaultMessage: 'Internal' },
    navKey: NAV_KEY.INTERNAL,
    children: [
      {
        message: { id: '...', defaultMessage: 'Components' },
        path: '/component-examples',
        userHasAccess: iaLogic.userHasAccess(IA_KEY.COMPONENT_EXAMPLES),
      },
    ],
  },
];

/**
 * The `makeShouldRender` function uses `await` for child navigation items that may return promises.
 * This is common in `userHasFeature` checks which may call Optimizely's `isFeatureEnabled` function, which returns a promise.
 * It ensures all promises resolve in child nav items for accurate rendering of parent navigation.
 */
export const isAllowedByExperience = (config, venue) => {
  const { children, navKey } = config;
  if (!_isEmpty(children)) {
    return children.some(child => isAllowedByExperience(child, venue));
  }
  return !navKey || experienceAllows(venue, navKey);
};

export const makeShouldRender = (userInfo, activeVenueId, locale) => async config => {
  const { userHasFeature, userHasAccess, children, navKey } = config;

  if (!_isEmpty(children)) {
    return (await Promise.all(children.map(makeShouldRender(userInfo, activeVenueId, locale)))).some(Boolean);
  }

  if (navKey && !experienceAllows(getActiveVenue(userInfo, activeVenueId), navKey)) {
    return false;
  }

  if (userHasFeature) {
    if (!userHasAccess) {
      return userHasFeature(userInfo, locale);
    }
    return (await userHasFeature(userInfo, locale, activeVenueId)) && userHasAccess(userInfo, activeVenueId);
  }

  if (!userHasAccess) return true;

  return userHasAccess(userInfo, activeVenueId);
};
